import React, { useMemo, useState } from 'react';
import { Box, Typography, LinearProgress, useMediaQuery, InputBase } from '@mui/material';
import { Search, Play, Pause, Square, Layers } from 'lucide-react';
import GlassPaper from '../../GlassPaper';
import StatPill from './StatPill';

function statusTone(s) {
    if (s === 'running') return 'good';
    if (s === 'stopped') return 'muted';
    if (s === 'paused' || s === 'suspended') return 'warn';
    return 'crit';
}
function statusIcon(s) {
    if (s === 'running') return <Play size={10} />;
    if (s === 'paused' || s === 'suspended') return <Pause size={10} />;
    return <Square size={10} />;
}
function fmtUptime(sec) {
    if (!sec) return '—';
    const d = Math.floor(sec / 86400);
    if (d > 0) return `${d}d`;
    const h = Math.floor((sec % 86400) / 3600);
    return `${h}h`;
}
function utilColor(v) {
    if (v == null) return '#64748b';
    if (v >= 85) return '#ef4444';
    if (v >= 70) return '#f59e0b';
    return '#10b981';
}

export default function VmStatusTable({ vms = [] }) {
    const [query, setQuery] = useState('');
    const isMobile = useMediaQuery('(max-width:600px)');
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return vms;
        return vms.filter(v =>
            String(v.name || '').toLowerCase().includes(q)
            || String(v.vmid).includes(q)
            || String(v.node || '').toLowerCase().includes(q)
            || String(v.type || '').toLowerCase().includes(q)
        );
    }, [vms, query]);

    const running = vms.filter(v => v.status === 'running').length;

    return (
        <GlassPaper sx={{ p: { xs: 1.5, sm: 2 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, gap: 1, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Layers size={16} />
                    <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Virtual Machines & Containers</Typography>
                    <StatPill tone="info" size="sm">{running}/{vms.length} running</StatPill>
                </Box>
                <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 0.5,
                    border: '1px solid rgba(148,163,184,0.22)',
                    borderRadius: 999, px: 1.25, py: 0.25,
                    minWidth: { xs: '100%', sm: 220 },
                }}>
                    <Search size={14} />
                    <InputBase
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Filter…"
                        sx={{ fontSize: 13, flex: 1 }}
                    />
                </Box>
            </Box>

            {/* Desktop / tablet: table-ish grid rows */}
            {!isMobile && (
                <Box sx={{ overflow: 'auto' }}>
                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: '2fr 1fr 80px 80px 1fr 1fr 60px',
                        gap: 1,
                        alignItems: 'center',
                        px: 1, py: 0.75,
                        fontSize: 10.5,
                        textTransform: 'uppercase',
                        letterSpacing: 0.8,
                        opacity: 0.65,
                        fontWeight: 700,
                        borderBottom: '1px solid rgba(148,163,184,0.18)',
                    }}>
                        <Box>Name</Box>
                        <Box>Node</Box>
                        <Box>VMID</Box>
                        <Box>Status</Box>
                        <Box>CPU</Box>
                        <Box>Memory</Box>
                        <Box sx={{ textAlign: 'right' }}>Up</Box>
                    </Box>
                    {filtered.map(v => (
                        <Box key={v.vmid} sx={{
                            display: 'grid',
                            gridTemplateColumns: '2fr 1fr 80px 80px 1fr 1fr 60px',
                            gap: 1,
                            alignItems: 'center',
                            px: 1, py: 1,
                            borderRadius: 2,
                            transition: 'background 180ms ease',
                            '&:hover': { background: 'rgba(148,163,184,0.08)' },
                        }}>
                            <Box>
                                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{v.name}</Typography>
                                <Typography sx={{ fontSize: 10.5, opacity: 0.55, textTransform: 'uppercase', letterSpacing: 0.5 }}>{v.type}</Typography>
                            </Box>
                            <Typography sx={{ fontSize: 12.5, opacity: 0.85 }}>{v.node}</Typography>
                            <Typography sx={{ fontSize: 12, fontVariantNumeric: 'tabular-nums', opacity: 0.7 }}>{v.vmid}</Typography>
                            <StatPill tone={statusTone(v.status)} icon={statusIcon(v.status)}>{v.status}</StatPill>
                            <UtilBar value={v.cpuPct} />
                            <UtilBar value={v.memPct} />
                            <Typography sx={{ fontSize: 12, fontVariantNumeric: 'tabular-nums', opacity: 0.75, textAlign: 'right' }}>{fmtUptime(v.uptimeSec)}</Typography>
                        </Box>
                    ))}
                </Box>
            )}

            {/* Mobile: stacked cards */}
            {isMobile && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {filtered.map(v => (
                        <Box key={v.vmid} sx={{
                            p: 1.25,
                            borderRadius: 2,
                            background: 'rgba(148,163,184,0.06)',
                            border: '1px solid rgba(148,163,184,0.12)',
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                                <Box>
                                    <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{v.name}</Typography>
                                    <Typography sx={{ fontSize: 10.5, opacity: 0.55 }}>{v.type} · {v.node} · #{v.vmid}</Typography>
                                </Box>
                                <StatPill tone={statusTone(v.status)} icon={statusIcon(v.status)}>{v.status}</StatPill>
                            </Box>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 60px', gap: 1, alignItems: 'center' }}>
                                <UtilBar value={v.cpuPct} label="CPU" />
                                <UtilBar value={v.memPct} label="Mem" />
                                <Typography sx={{ fontSize: 11.5, opacity: 0.7, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtUptime(v.uptimeSec)}</Typography>
                            </Box>
                        </Box>
                    ))}
                </Box>
            )}

            {filtered.length === 0 && (
                <Typography sx={{ opacity: 0.55, textAlign: 'center', py: 3, fontSize: 13 }}>No VMs match.</Typography>
            )}
        </GlassPaper>
    );
}

function UtilBar({ value, label }) {
    const v = Number.isFinite(value) ? Math.max(0, Math.min(100, value)) : null;
    return (
        <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, opacity: 0.7, mb: 0.25 }}>
                {label && <Box sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 600 }}>{label}</Box>}
                <Box sx={{ fontVariantNumeric: 'tabular-nums', ml: 'auto' }}>{v != null ? `${v.toFixed(0)}%` : '—'}</Box>
            </Box>
            <LinearProgress
                variant="determinate"
                value={v ?? 0}
                sx={{
                    height: 6,
                    borderRadius: 999,
                    bgcolor: 'rgba(148,163,184,0.15)',
                    '& .MuiLinearProgress-bar': {
                        borderRadius: 999,
                        backgroundColor: utilColor(v),
                    },
                }}
            />
        </Box>
    );
}
